import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

interface Point { id: string; name: string }
interface Tariff { id: string; name: string; bikeType: string; color: string | null }
interface Bike {
  id: string
  code: string
  model: string | null
  status: 'free' | 'reserved' | 'in_use' | 'service'
  pointId: string | null
  point: Point | null
  tariff: Tariff | null
  notes: string | null
}

const STATUS_LABEL: Record<Bike['status'], string> = {
  free: 'Свободен',
  reserved: 'Резерв',
  in_use: 'В аренде',
  service: 'Сервис',
}

const STATUS_COLOR: Record<Bike['status'], string> = {
  free: 'bg-green-100 text-green-700',
  reserved: 'bg-yellow-100 text-yellow-700',
  in_use: 'bg-blue-100 text-blue-700',
  service: 'bg-red-100 text-red-700',
}

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } }
  return e?.response?.data?.error?.message ?? 'Ошибка'
}

export default function MechanicBikesPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [pointFilter, setPointFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<Bike['status'] | ''>('')
  const [editing, setEditing] = useState<Bike | null>(null)

  const { data: bikesData, isLoading } = useQuery<{ bikes: Bike[] }>({
    queryKey: ['mechanic-bikes'],
    queryFn: () => api.get('/bikes').then((r) => r.data),
    staleTime: 30_000,
  })

  const { data: pointsData } = useQuery<{ points: Point[] }>({
    queryKey: ['points'],
    queryFn: () => api.get('/points').then((r) => r.data),
  })

  const bikes = bikesData?.bikes ?? []
  const points = pointsData?.points ?? []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return bikes.filter((b) => {
      if (pointFilter && b.pointId !== pointFilter) return false
      if (statusFilter && b.status !== statusFilter) return false
      if (q && !b.code.toLowerCase().includes(q) && !(b.model ?? '').toLowerCase().includes(q)) return false
      return true
    })
  }, [bikes, search, pointFilter, statusFilter])

  const counts = useMemo(() => {
    const c = { free: 0, reserved: 0, in_use: 0, service: 0 } as Record<Bike['status'], number>
    for (const b of bikes) c[b.status]++
    return c
  }, [bikes])

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Велосипеды</h1>
        <span className="text-xs text-gray-500">Всего: {bikes.length}</span>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-4 gap-2">
        {(['free', 'reserved', 'in_use', 'service'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            className={`rounded-xl px-2 py-2 text-center border transition-colors ${
              statusFilter === s ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'
            }`}
          >
            <div className={`text-xs ${STATUS_COLOR[s].split(' ')[1]}`}>{STATUS_LABEL[s]}</div>
            <div className="text-lg font-semibold">{counts[s]}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Код или модель"
          className="border rounded-xl px-3 py-2 text-sm"
        />
        <select value={pointFilter} onChange={(e) => setPointFilter(e.target.value)} className="border rounded-xl px-3 py-2 text-sm">
          <option value="">Все точки</option>
          {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="space-y-2">
        {isLoading && <div className="text-center text-sm text-gray-500 py-6">Загрузка…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-6">Нет велосипедов под фильтр</div>
        )}
        {filtered.map((b) => (
          <button
            key={b.id}
            onClick={() => setEditing(b)}
            className="w-full bg-white border rounded-xl p-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold">#{b.code}</span>
                {b.model && <span className="text-sm text-gray-600 truncate">{b.model}</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {b.point?.name ?? '— без точки'}
                {b.tariff && <span> · {b.tariff.name}</span>}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[b.status]} shrink-0`}>
              {STATUS_LABEL[b.status]}
            </span>
          </button>
        ))}
      </div>

      {editing && (
        <BikeEditModal
          bike={editing}
          points={points}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['mechanic-bikes'] })
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

function BikeEditModal({ bike, points, onClose, onSaved }: {
  bike: Bike
  points: Point[]
  onClose: () => void
  onSaved: () => void
}) {
  const [status, setStatus] = useState<Bike['status']>(bike.status)
  const [pointId, setPointId] = useState<string>(bike.pointId ?? '')
  const [notes, setNotes] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // In-use bikes shouldn't be moved/changed by mechanic — block with a friendly message
  const isInUse = bike.status === 'in_use'

  const statusMutation = useMutation({
    mutationFn: () => api.patch(`/bikes/${bike.id}/status`, { status, notes: notes.trim() || undefined }),
    onError: (e) => setError(apiError(e)),
  })

  const pointMutation = useMutation({
    mutationFn: () => api.patch(`/bikes/${bike.id}`, { pointId: pointId || null }),
    onError: (e) => setError(apiError(e)),
  })

  const handleSave = async () => {
    setError(null)
    const tasks: Promise<unknown>[] = []
    if (status !== bike.status) tasks.push(statusMutation.mutateAsync())
    if ((pointId || null) !== (bike.pointId ?? null)) tasks.push(pointMutation.mutateAsync())
    if (tasks.length === 0) {
      onClose()
      return
    }
    try {
      await Promise.all(tasks)
      onSaved()
    } catch {
      // mutation onError already set the message
    }
  }

  const pending = statusMutation.isPending || pointMutation.isPending

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div className="bg-white w-full rounded-t-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-base">Велосипед #{bike.code}</h3>
            {bike.model && <p className="text-xs text-gray-500">{bike.model}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
        </div>

        {isInUse && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-xs text-yellow-800">
            Велосипед сейчас в аренде. Сначала его должен закрыть менеджер.
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
          <div className="grid grid-cols-2 gap-2">
            {(['free', 'reserved', 'service'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                disabled={isInUse}
                className={`px-3 py-2 rounded-xl text-sm border transition-colors disabled:opacity-50 ${
                  status === s ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
            {status === 'in_use' && (
              <div className="col-span-2 text-xs text-gray-500 text-center py-2">
                Статус «В аренде» снимается автоматически при закрытии аренды
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Точка</label>
          <select
            value={pointId}
            onChange={(e) => setPointId(e.target.value)}
            disabled={isInUse}
            className="w-full border rounded-xl px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">— Без точки —</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {status === 'service' && status !== bike.status && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Заметка (по желанию)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Что случилось / что чинили"
              className="w-full border rounded-xl px-3 py-2 text-sm"
            />
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-300 text-sm">Отмена</button>
          <button
            onClick={handleSave}
            disabled={pending || isInUse}
            className="flex-1 py-3 rounded-xl bg-brand-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {pending ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
